import React, { useState } from 'react';
import { Send, PlusCircle, Link, FileText, HelpCircle } from 'lucide-react';
import AgentMessage from './AgentMessage';
import ExamplesModal from './ExamplesModal';
type Message = {
  id: string;
  content: string;
  sender: 'user' | 'agent';
  timestamp: Date;
  resources?: {
    type: 'link' | 'document' | 'connection';
    title: string;
    url?: string;
  }[];
};
const AgentInterface = () => {
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    content: "Hello! I'm your professional growth assistant. I can help you develop your skills, connect you with resources, and support you in resolving tasks. What would you like to do?",
    sender: 'agent',
    timestamp: new Date()
  }]);
  const [inputValue, setInputValue] = useState('');
  const [showExamplesModal, setShowExamplesModal] = useState(false);
  const handleSendMessage = () => {
    if (!inputValue.trim()) return;
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    // Simulate agent response (would be replaced with actual API call)
    setTimeout(() => {
      const agentResponse: Message = {
        id: (Date.now() + 1).toString(),
        content: getAgentResponse(inputValue),
        sender: 'agent',
        timestamp: new Date(),
        resources: getRandomResources()
      };
      setMessages(prev => [...prev, agentResponse]);
    }, 1000);
  };
  // Simulated responses - would be replaced with actual AI responses
  const getAgentResponse = (query: string): string => {
    if (query.toLowerCase().includes('leadership')) {
      return 'Leadership skills are crucial for career advancement. I recommend focusing on communication, delegation, and strategic thinking. Would you like specific resources on any of these areas?';
    } else if (query.toLowerCase().includes('technical') || query.toLowerCase().includes('coding')) {
      return "For technical skill development, it's important to balance theory with practical application. Consider project-based learning and connecting with mentors in your field. I've attached some relevant resources.";
    } else {
      return "That's an interesting area for growth. I've identified some resources that might help you develop in this direction. Would you like me to connect you with someone in your organization who has expertise in this area?";
    }
  };
  const getRandomResources = () => {
    const resourceTypes = [{
      type: 'link' as const,
      title: 'Harvard Business Review: Leadership Fundamentals',
      url: 'https://example.com/leadership'
    }, {
      type: 'document' as const,
      title: 'Technical Skill Development Roadmap',
      url: 'https://example.com/document'
    }, {
      type: 'connection' as const,
      title: 'Sarah Chen - Director of Engineering'
    }];
    // Return 1-2 random resources
    return resourceTypes.sort(() => 0.5 - Math.random()).slice(0, Math.floor(Math.random() * 2) + 1);
  };
  return <div className="flex flex-col h-full border rounded-lg bg-white shadow-sm overflow-hidden" data-id="element-23">
      <div className="px-4 py-3 border-b bg-gray-50" data-id="element-24">
        <h2 className="text-lg font-medium text-gray-900" data-id="element-25">Growth Agent</h2>
        <p className="text-sm text-gray-500" data-id="element-26">
          Get personalized advice and resources for your professional
          development
        </p>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-27">
        {messages.map(message => <AgentMessage key={message.id} message={message} data-id="element-28" />)}
      </div>
      <div className="border-t p-4" data-id="element-29">
        <div className="flex space-x-2" data-id="element-30">
          <div className="flex-1" data-id="element-31">
            <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSendMessage()} placeholder="Ask about skill development, resources, or connections..." className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-32" />
          </div>
          <button onClick={handleSendMessage} className="bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-33">
            <Send className="w-5 h-5" data-id="element-34" />
          </button>
        </div>
        <div className="flex mt-2 space-x-2" data-id="element-35">
          <button className="text-sm text-gray-600 flex items-center hover:text-blue-600" data-id="element-36">
            <PlusCircle className="w-4 h-4 mr-1" data-id="element-37" />
            <span data-id="element-38">New skill</span>
          </button>
          <button className="text-sm text-gray-600 flex items-center hover:text-blue-600" data-id="element-39">
            <Link className="w-4 h-4 mr-1" data-id="element-40" />
            <span data-id="element-41">Add connection</span>
          </button>
          <button className="text-sm text-gray-600 flex items-center hover:text-blue-600" data-id="element-42">
            <FileText className="w-4 h-4 mr-1" data-id="element-43" />
            <span data-id="element-44">Upload document</span>
          </button>
          <button onClick={() => setShowExamplesModal(true)} className="text-sm text-gray-600 flex items-center hover:text-blue-600" data-id="element-45">
            <HelpCircle className="w-4 h-4 mr-1" data-id="element-46" />
            <span data-id="element-47">Examples</span>
          </button>
        </div>
      </div>
      {/* Examples Modal */}
      {showExamplesModal && <ExamplesModal onClose={() => setShowExamplesModal(false)} data-id="element-48" />}
    </div>;
};
export default AgentInterface;